import React, { useContext, useEffect } from "react";
import PropTypes from "prop-types";
import { formatDate } from "utils/dates";
import { PureModal, Rows, InlineWaiting, IconButton } from "components/ui";
import { MdComment, MdAdd, MdLink, MdEdit } from "react-icons/lib/md";
import TaskForm from "features/tasks/task-form";
import AddTaskNoteForm from "./add-task-note";
import PatientTaskFilters from "./task-filters";
import TaskAddressUpdateForm from "./task-address-update-form";
import useLocalQuery from "hooks/useQuery/local-query";
import useTasks from "features/tasks/use-tasks";
import { PatientProfileContext } from "components/profile/context";
import { Link } from "react-router-dom";
import AudioPlayer from "components/ui/audio-player";
import FormikField from "components/forms/formik-field";
import { Formik, Form, Field } from "formik";
import RadioGroupField from "components/forms/radio-group-field";
import SearchableDropdown from "components/searchable-dropdown";
import * as Yup from "yup";
const FormUpdater = ({ owner, setFieldValue }) => {
  useEffect(() => {
    setFieldValue("assigned_to_guid", "");
  }, [owner]);
  return null;
};

const PatientTasks = ({ patientId, patientInfo }) => {
  const {
    tasks,
    createTask,
    createTaskNote,
    getPatientTasks,
    updateTask,
    isFetchingTasks
  } = useTasks(patientId);
  const { accessible_users } = useContext(PatientProfileContext);
  const { rows, query, queryDispatcher } = useLocalQuery(tasks, {
    sort: { by: "created_at", direction: "desc" }, filters: { status:["Pending", "Created"]},
  });
  React.useEffect(() => {
    getPatientTasks(patientId);
  }, [patientId]);

  return (
    <div className="profile-tasks">
      <div style={{ display: "grid", gridTemplateColumns: "200px 1fr" }}>
        <PureModal
          renderTrigger={({ openModal }) => (
            <div
              style={{
                display: "flex",
                padding: 5,
                flex: 1,
                alignItems: "center"
              }}
            >
              <IconButton
                onClick={openModal}
                icon={<MdAdd />}
                text="Create Task"
                style={{
                  margin: 0,
                  width: "100%",
                  padding: "10px 10px",
                  color: "#085b55"
                }}
              />
            </div>
          )}
          renderContent={({ closeModal }) => (
            <TaskForm
              accessible_users={accessible_users}
              patientId={patientId}
              patientStage={patientInfo.stage_id}
              createTask
              handleSubmit={async values => {
                await createTask(values);
                await getPatientTasks(patientId);
                closeModal();
              }}
            />
          )}
        />
        <PatientTaskFilters query={query} queryDispatcher={queryDispatcher} />
      </div>
      {isFetchingTasks && (
        <div
          style={{
            display: "flex",
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            padding: 30
          }}
        >
          <InlineWaiting reason="Fetching Patient Tasks" />
        </div>
      )}
      {rows?.map(task => (
        <div
          key={task.task_id}
          style={{ border: "1px solid #ccc", padding: 5 }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto auto",
              flex: 1,
              justifyContent: "space-between",
              fontSize: 18
            }}
          >
            <div style={{ marginBottom: 5 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 100px",
                  alignItems: "center"
                }}
              >
                <h4 style={{ margin: 3 }}>{task.action_text}</h4>
                {task.type == "Order" && (
                  <div>
                    <Link to={`/orders/${task.order_id}`}>
                      <MdLink />
                      Order
                    </Link>
                  </div>
                )}
                {task.signed_recording_url && (
                  <AudioPlayer
                    preloadAudio={"none"}
                    src={task.signed_recording_url}
                    showAllFeatures={false}
                    showTableFormatted={true}
                  />
                )}
              </div>
              <div
                style={{
                  display: "flex"
                }}
              >
                <h5 style={{ margin: 0 }}>{task.text}</h5>
              </div>
            </div>
          </div>
          <Formik
            initialValues={{
              ...task,
              patientId
            }}
            validationSchema={Yup.object().shape({
              type: Yup.string().required("Required"),
              action_id: Yup.string().required("Required")
            })}
            onSubmit={async values => {
              await updateTask({ ...values, patientId });
              await getPatientTasks(patientId);
            }}
            enableReinitialize
          >
            {({ values, setFieldValue, handleSubmit  }) => (
              <Form>
                <FormUpdater owner={values.owner} setFieldValue={setFieldValue} />
                <div
                  style={{
                    display: "flex",
                    flex: 1,
                    justifyContent: "space-between"
                  }}
                >
                  
                  <div>
                    <div
                        style={{
                          display: "flex",
                          paddingTop: 10,
                          paddingBottom: 5,
                          flex: 1
                        }}
                      >
                      <label style={{ width: 160 }}>Status:</label>
                      <Field
                        as="select"
                        name="status"
                        onChange={e => {
                          setFieldValue("status", e.target.value);
                          handleSubmit(); 
                        }}
                      >
                        <option value="Pending">Pending</option>
                        <option value="Completed">Completed</option>
                      </Field>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        paddingTop: 5,
                        paddingBottom: 10,
                        flex: 1
                      }}
                    >
                      <label style={{ width: 160 }}>Assigned To:</label>
                      <SearchableDropdown
                        options={[
                          { text: "", value: "", key: "placeholder", disabled: true },
                          ...accessible_users.dmes
                        ]}
                        selected={task.assigned_to_guid || ""}
                        onSelect={value => {
                          setFieldValue("assigned_to_guid", value);
                          handleSubmit(); 
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label>Created At: {formatDate(task.created_at)}</label>
                    <label>Created By: {task.created_by_name}</label>

                    <label>
                      Created by form:
                      <span style={{ fontWeight: "bold", paddingLeft: 5 }}>
                        {task.CreatedByForm ? "Yes" : "No"}
                      </span>
                    </label>
                    {task.CreatedByForm && (
                      <>
                        <label>
                          Created by question:
                          <span style={{ fontWeight: "bold", paddingLeft: 5 }}>
                            {task.CreatedByQuestion}
                          </span>
                        </label>
                        <label>
                          Created by answer:
                          <span style={{ fontWeight: "bold", paddingLeft: 5 }}>
                            {task.CreatedByAnswer}
                          </span>
                        </label>
                      </>
                    )}
                  </div>
                </div>
              </Form>
            )}
          </Formik>
          {/* {JSON.stringify(task)} */}
          {/* Address Update Form for address change tasks */}
          {(task.action_text === "AC - Perm Address Change" || 
            task.action_text === "TAC - Temporary Address Change") && (
            <div style={{ marginTop: 10, marginBottom: 10, padding: 10, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
              <TaskAddressUpdateForm 
                patientId={patientId}
                orderId={task.order_id}
                taskId={task.task_id}
                taskType={task.action_text}
                taskText={task.text}
                onSuccess={async () => {
                  // Optionally mark task as completed after address update
                  await updateTask({ ...task, status: 'Completed', patientId });
                  await getPatientTasks(patientId);
                }}
              />
            </div>
          )}
          
          <div>
            <Rows>
              {task.notes?.map(({ id, text, created_by_name, created_at }) => (
                <div
                  className="worklist-item note"
                  key={id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    alignItems: "center",
                    gridGap: "10px",
                    fontSize: "1.15em"
                  }}
                >
                  <span
                    className="order-note"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "auto 1fr",
                      alignItems: "center",
                      gridGap: "10px",
                      padding: "0 0px",
                      margin: "0 5px",
                      color: "#444"
                    }}
                  >
                    <MdComment />
                    <span
                      className="note-content"
                      style={{
                        padding: "0 7px",
                        marginRight: "10px",
                        color: "black"
                      }}
                    >
                      {text}
                    </span>
                  </span>
                  <div
                    className="worklist-item-meta"
                    style={{
                      flex: "1 1 auto",
                      fontSize: "0.8em",
                      textAlign: "right",
                      marginLeft: "10px",
                      marginRight: "10px"
                    }}
                  >
                    <div className="creator">{created_by_name}</div>
                    <div>{formatDate(created_at, "MMMM Do YYYY, h:mm a")}</div>
                  </div>
                </div>
              ))}
            </Rows>
            <AddTaskNoteForm
              task={task}
              patientId={patientId}
              createTaskNote={async values => {
                await createTaskNote(values);
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

PatientTasks.propTypes = {
  patientId: PropTypes.string.isRequired,
  patientInfo: PropTypes.object
};

export default PatientTasks;
